import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, CardContent, CircularProgress, Grid, Typography } from "@mui/material";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import companyServices from "app/services/company-services";
import RoutingSettingsDialog from "./RoutingSettingsDialog";

// Resumen de cómo rutea la empresa, con el botón para cambiarlo.
//
// Muestra los valores efectivos y no el jsonb crudo: lo que está vacío en la
// configuración igual tiene un comportamiento, y esconderlo obliga a saberse los
// defaults de memoria.
const RESUMEN = [
    { key: "sequencer", label: "Motor", formato: (v) => (v === "local" ? "Propio (óptimo exacto)" : "HERE") },
    { key: "return_to_depot", label: "Vuelve a la bodega", formato: (v) => (v ? "Sí" : "No, termina en la última parada") },
    { key: "objective", label: "Optimiza por", formato: (v) => (v === "distance" ? "Distancia" : "Tiempo") },
    { key: "service_time_mode", label: "Tiempo por parada", formato: (v, s) => (v === "learned" ? "Aprendido por dirección" : `Fijo, ${s.default_service_time_min ?? 30} min`) },
    { key: "workday_start", label: "Jornada", formato: (v, s) => (v && s.workday_end ? `${v} a ${s.workday_end}${s.enforce_workday ? "" : " (sin marcar)"}` : "Sin definir") },
    { key: "fuel_cost_per_km", label: "Combustible", formato: (v, s) => (v ? `$${Number(v).toLocaleString("es-CL")} por km${s.fuel_km_per_liter ? ` (${s.fuel_km_per_liter} km/l)` : ""}` : "Sin definir") },
    { key: "driver_cost_per_hour", label: "Chofer", formato: (v, s) => (v ? `$${Number(v).toLocaleString("es-CL")} por hora${s.driver_net_salary ? ` (líquido $${Number(s.driver_net_salary).toLocaleString("es-CL")})` : ""}` : "Sin definir") },
];

const RoutingSettingsPanel = ({ companyId }) => {
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const cargar = useCallback(() => {
        if (!companyId) return;

        setLoading(true);
        companyServices.getCompanySettings(companyId)
            .then((data) => setCompany(data))
            .catch(() => setCompany(null))
            .finally(() => setLoading(false));
    }, [companyId]);

    useEffect(() => { cargar(); }, [cargar]);

    if (loading) return <CircularProgress size={24} sx={{ mt: 2 }} />;
    if (!company) return <Alert severity="error" sx={{ mt: 2 }}>No se pudo cargar la configuración.</Alert>;

    // Lo efectivo lo resuelve el backend: acá no se repiten los valores por defecto,
    // que es la forma segura de que se desalineen.
    const settings = company.routing_settings_effective || company.routing_settings || {};

    return (
        <React.Fragment>
            <Card sx={{ mt: 1 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        {RESUMEN.map((fila) => (
                            <Grid item xs={12} sm={6} md={4} key={fila.key}>
                                <Typography variant="caption" color="text.secondary">{fila.label}</Typography>
                                <Typography variant="body2">{fila.formato(settings[fila.key], settings)}</Typography>
                            </Grid>
                        ))}
                    </Grid>

                    <Button
                        variant="contained" size="small" sx={{ mt: 3 }}
                        startIcon={<AltRouteOutlinedIcon />}
                        onClick={() => setOpen(true)}>
                            Cambiar configuración
                    </Button>

                    <Alert severity="info" sx={{ mt: 3 }}>
                        <Typography variant="body2">
                            Cada vehículo puede pisar estas opciones desde su ficha. Lo que no personalice, lo hereda de acá.
                        </Typography>
                    </Alert>
                </CardContent>
            </Card>

            <RoutingSettingsDialog
                open={open}
                onClose={() => setOpen(false)}
                scope="company"
                target={company}
                onSaved={cargar}
            />
        </React.Fragment>
    );
};

export default RoutingSettingsPanel;
